import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Cake, Calendar as CalendarIcon, RotateCcw, Star, Moon } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { ResultDisplay } from "@/components/calculator/ResultDisplay";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { DateField } from "@/components/calculator/DateField";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  bengaliAge,
  computeAge,
  formatPrimaryAge,
  hijriAge,
  validateAgeInputs,
  type AgeInputError,
  type ZodiacKey,
} from "@/lib/calculators/age";

/* ------------ Strings ------------ */

const STR = {
  h1: { en: "Age Calculator", bn: "বয়স ক্যালকুলেটর" },
  desc: {
    en: "Calculate your exact age in years, months, days, hours and minutes — with Bengali calendar (Bangla shon) and Hijri age.",
    bn: "বছর, মাস, দিন, ঘণ্টা ও মিনিটে আপনার সঠিক বয়স — বাংলা সন ও হিজরি বয়স সহ।",
  },
  dob: { en: "Date of Birth", bn: "জন্ম তারিখ" },
  asOf: { en: "Calculate Age As Of", bn: "যে তারিখে হিসাব" },
  calendarType: { en: "Calendar", bn: "ক্যালেন্ডার" },
  gregorian: { en: "Gregorian", bn: "গ্রেগোরিয়ান" },
  bengali: { en: "Bengali", bn: "বাংলা" },
  hijri: { en: "Hijri", bn: "হিজরি" },
  reset: { en: "Reset", bn: "রিসেট" },
  calculate: { en: "Calculate", bn: "হিসাব করুন" },
  yourAge: { en: "Your Age", bn: "আপনার বয়স" },
  totalMonths: { en: "Total Months", bn: "মোট মাস" },
  totalWeeks: { en: "Total Weeks", bn: "মোট সপ্তাহ" },
  totalDays: { en: "Total Days", bn: "মোট দিন" },
  totalHours: { en: "Total Hours", bn: "মোট ঘণ্টা" },
  nextBirthday: { en: "Next birthday", bn: "পরবর্তী জন্মদিন" },
  daysUntil: { en: "in {n} days", bn: "{n} দিনে" },
  on: { en: "on", bn: "—" },
  dayBorn: { en: "Day born", bn: "জন্মের দিন" },
  zodiac: { en: "Zodiac", bn: "রাশি" },
  bnAgeLabel: { en: "Bengali calendar age", bn: "বাংলা সন বয়স" },
  hijriAgeLabel: { en: "Hijri age", bn: "হিজরি বয়স" },
  banglaShon: { en: "Bangla shon", bn: "বাংলা সন" },
  years: { en: "years", bn: "বছর" },
  shareLine: { en: "Share your result", bn: "ফলাফল শেয়ার করুন" },

  // Errors
  err_future_dob: { en: "Birth date cannot be in the future", bn: "জন্ম তারিখ ভবিষ্যতের হতে পারে না" },
  err_invalid_dob: { en: "Please enter a valid date", bn: "দয়া করে একটি বৈধ তারিখ লিখুন" },
  err_asof_before_dob: {
    en: "End date cannot be before birth date",
    bn: "শেষ তারিখ জন্ম তারিখের আগে হতে পারে না",
  },
  err_out_of_range: { en: "Date is out of supported range", bn: "তারিখ সমর্থিত সীমার বাইরে" },

  // Content
  howUseTitle: { en: "How to use", bn: "কীভাবে ব্যবহার করবেন" },
  howUse: {
    en: [
      "Enter your date of birth using the calendar or type DD/MM/YYYY.",
      "Optionally change the 'as of' date to calculate age on a different day.",
      "Switch the calendar toggle for Bengali (Bangla shon) or Hijri age.",
    ],
    bn: [
      "ক্যালেন্ডার থেকে অথবা DD/MM/YYYY ফরম্যাটে আপনার জন্ম তারিখ লিখুন।",
      "অন্য তারিখে বয়স জানতে চাইলে 'যে তারিখে হিসাব' পরিবর্তন করুন।",
      "বাংলা সন বা হিজরি বয়সের জন্য ক্যালেন্ডার টগল ব্যবহার করুন।",
    ],
  },
  howCalcTitle: { en: "How is age calculated?", bn: "বয়স কীভাবে হিসাব হয়?" },
  howCalc: {
    en: "We compute the full years between your birth date and the chosen date, then the remaining months, then the remaining days. We also report total months, weeks, days, hours and minutes for completeness.",
    bn: "প্রথমে পূর্ণ বছর হিসাব হয়, এরপর বাকি মাস, এরপর বাকি দিন। মোট মাস, সপ্তাহ, দিন, ঘণ্টা ও মিনিটও দেখানো হয়।",
  },
  bnExplainTitle: { en: "Bengali calendar (Bangla shon) age", bn: "বাংলা সন বয়স" },
  bnExplain: {
    en: "The Bangladeshi civil Bengali calendar starts each year on Pohela Boishakh (April 14). Your Bangla shon birth year is converted from your Gregorian DOB and your Bengali age increases on each Bengali birthday.",
    bn: "বাংলাদেশের সরকারি বাংলা ক্যালেন্ডারে নতুন বছর শুরু হয় পহেলা বৈশাখে (১৪ এপ্রিল)। আপনার গ্রেগোরিয়ান জন্ম তারিখ থেকে বাংলা সন বের করে প্রতি বাংলা জন্মদিনে বয়স বাড়ে।",
  },
  faqTitle: { en: "Frequently Asked Questions", bn: "প্রায়শই জিজ্ঞাসিত প্রশ্ন" },
};

const ZODIAC_LABEL: Record<ZodiacKey, { en: string; bn: string; symbol: string }> = {
  aries:       { en: "Aries",       bn: "মেষ",     symbol: "♈" },
  taurus:      { en: "Taurus",      bn: "বৃষ",     symbol: "♉" },
  gemini:      { en: "Gemini",      bn: "মিথুন",   symbol: "♊" },
  cancer:      { en: "Cancer",      bn: "কর্কট",   symbol: "♋" },
  leo:         { en: "Leo",         bn: "সিংহ",    symbol: "♌" },
  virgo:       { en: "Virgo",       bn: "কন্যা",   symbol: "♍" },
  libra:       { en: "Libra",       bn: "তুলা",    symbol: "♎" },
  scorpio:     { en: "Scorpio",     bn: "বৃশ্চিক", symbol: "♏" },
  sagittarius: { en: "Sagittarius", bn: "ধনু",     symbol: "♐" },
  capricorn:   { en: "Capricorn",   bn: "মকর",     symbol: "♑" },
  aquarius:    { en: "Aquarius",    bn: "কুম্ভ",   symbol: "♒" },
  pisces:      { en: "Pisces",      bn: "মীন",     symbol: "♓" },
};

const FAQ = [
  {
    q: { en: "How is my age calculated?", bn: "আমার বয়স কীভাবে হিসাব হয়?" },
    a: {
      en: "We use the date-fns library to compute full years, then remaining months, then remaining days between your date of birth and the chosen date — the same way you'd say your age in conversation.",
      bn: "date-fns লাইব্রেরি ব্যবহার করে আমরা প্রথমে পূর্ণ বছর, এরপর বাকি মাস, এরপর বাকি দিন বের করি — যেভাবে আপনি কথায় বয়স বলেন।",
    },
  },
  {
    q: { en: "What is my Bengali calendar age?", bn: "আমার বাংলা সন বয়স কী?" },
    a: {
      en: "Your Gregorian DOB is converted to the Bangladeshi civil Bengali calendar (Bangla shon). Your Bengali age increases each year on your Bengali birthday — typically the same calendar day, since the conversion is fixed.",
      bn: "আপনার গ্রেগোরিয়ান জন্ম তারিখকে বাংলাদেশের সরকারি বাংলা ক্যালেন্ডারে রূপান্তর করা হয়। প্রতি বাংলা জন্মদিনে বাংলা বয়স এক বছর বাড়ে।",
    },
  },
  {
    q: { en: "How accurate is this calculator?", bn: "এই ক্যালকুলেটর কতটা সঠিক?" },
    a: {
      en: "The calculation is exact to the second using the well-tested date-fns library. Hijri age uses the Umm al-Qura calendar via the browser's Intl API.",
      bn: "ব্যাপকভাবে পরীক্ষিত date-fns ব্যবহার করে গণনা সেকেন্ড পর্যন্ত নির্ভুল। হিজরি বয়সের জন্য ব্রাউজারের Intl API দিয়ে উম্মুল-কুরা ক্যালেন্ডার ব্যবহার হয়।",
    },
  },
  {
    q: { en: "Can I calculate age between two dates?", bn: "দুই তারিখের মধ্যে বয়স কি বের করা যায়?" },
    a: {
      en: "Yes — change the 'Calculate Age As Of' field to any past or future date and the result updates automatically.",
      bn: "হ্যাঁ — 'যে তারিখে হিসাব' ক্ষেত্রে যেকোনো অতীত বা ভবিষ্যৎ তারিখ দিন; ফলাফল স্বয়ংক্রিয়ভাবে আপডেট হবে।",
    },
  },
  {
    q: { en: "How is a leap year birthday (Feb 29) handled?", bn: "অধিবর্ষ জন্মদিন (২৯ ফেব্রুয়ারি) কীভাবে হ্যান্ডেল হয়?" },
    a: {
      en: "If you were born on February 29, your next birthday is shown on February 28 in non-leap years and on February 29 in leap years.",
      bn: "যদি ২৯ ফেব্রুয়ারিতে জন্ম, অধিবর্ষ ছাড়া বছরে পরবর্তী জন্মদিন ২৮ ফেব্রুয়ারি দেখানো হয়, অধিবর্ষে ২৯ ফেব্রুয়ারি।",
    },
  },
  {
    q: { en: "Is this calculator free?", bn: "এই ক্যালকুলেটর কি ফ্রি?" },
    a: {
      en: "Yes — completely free, no signup, no email required, and runs entirely in your browser.",
      bn: "হ্যাঁ — সম্পূর্ণ বিনামূল্যে, সাইন-আপ নেই, ইমেইল লাগবে না, এবং সম্পূর্ণ আপনার ব্রাউজারে চলে।",
    },
  },
];

/* ------------ Route ------------ */

export const Route = createFileRoute("/age-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Age Calculator — Calculate Your Exact Age",
      description:
        "Free age calculator: exact age in years, months, days, hours, minutes. Includes Bengali (Bangla shon) and Hijri age. Made for Bangladesh.",
      path: "/age-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Age Calculator", path: "/age-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: AgeCalculatorPage,
});

/* ------------ Page ------------ */

type CalendarKind = "gregorian" | "bengali" | "hijri";

function AgeCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const { t } = useT();
  const tr = (k: keyof typeof STR) => (STR[k] as { en: string; bn: string })[lang];
  const num = (n: number) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmtThousands = (n: number) => (lang === "bn" ? toBengaliNumerals(n.toLocaleString("en-IN")) : n.toLocaleString("en-IN"));

  const [dob, setDob] = useState<Date | null>(null);
  const [asOf, setAsOf] = useState<Date>(() => new Date());
  const [calendarKind, setCalendarKind] = useState<CalendarKind>("gregorian");

  const validation = useMemo(() => validateAgeInputs(dob, asOf), [dob, asOf]);

  const result = useMemo(() => {
    if (!validation.ok || !dob) return null;
    return computeAge(dob, asOf);
  }, [dob, asOf, validation.ok]);

  const bn = useMemo(() => (validation.ok && dob ? bengaliAge(dob, asOf) : null), [validation.ok, dob, asOf]);
  const hj = useMemo(() => (validation.ok && dob ? hijriAge(dob, asOf) : null), [validation.ok, dob, asOf]);

  const errKey = validation.error;
  const errMessage = errKey ? tr(("err_" + errKey) as keyof typeof STR) : null;

  // Auto-update asOf each minute so totals stay live
  useEffect(() => {
    const id = setInterval(() => setAsOf(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const reset = () => {
    setDob(null);
    setAsOf(new Date());
    setCalendarKind("gregorian");
  };

  const primaryByCalendar = (): { label: string; value: string } => {
    if (calendarKind === "bengali" && bn) {
      return {
        label: tr("bnAgeLabel"),
        value: lang === "bn"
          ? `${num(bn.age)} ${tr("years")} (${tr("banglaShon")}: ${num(bn.birthYear)} → ${num(bn.currentYear)})`
          : `${num(bn.age)} ${tr("years")} (${tr("banglaShon")}: ${num(bn.birthYear)} → ${num(bn.currentYear)})`,
      };
    }
    if (calendarKind === "hijri" && hj) {
      return {
        label: tr("hijriAgeLabel"),
        value: `${num(hj.age)} ${tr("years")} (${num(hj.birthYear)} AH → ${num(hj.currentYear)} AH)`,
      };
    }
    return { label: tr("yourAge"), value: "" };
  };

  const primaryLine = result ? formatPrimaryAge(result.breakdown, lang, num) : "";
  const altPrimary = primaryByCalendar();

  return (
    <CalculatorLayout
      slug="age-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Cake"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Inputs */}
      <div className="grid gap-5 md:grid-cols-2">
        <DateField
          label={tr("dob")}
          value={dob}
          onChange={setDob}
          error={errKey === "future_dob" || errKey === "out_of_range" ? errMessage : null}
          fromYear={1900}
          toDate={new Date()}
        />
        <DateField
          label={tr("asOf")}
          value={asOf}
          onChange={(d) => d && setAsOf(d)}
          error={errKey === "asof_before_dob" ? errMessage : null}
          fromYear={1900}
          toDate={new Date(2200, 11, 31)}
        />
      </div>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <UnitToggle<CalendarKind>
          label={tr("calendarType")}
          value={calendarKind}
          onChange={setCalendarKind}
          options={[
            { value: "gregorian", label: tr("gregorian") },
            { value: "bengali", label: tr("bengali") },
            { value: "hijri", label: tr("hijri") },
          ]}
        />
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} className="gap-2">
            <RotateCcw size={14} />
            {tr("reset")}
          </Button>
          <Button className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
            <Cake size={14} />
            {tr("calculate")}
          </Button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {calendarKind === "gregorian" ? (
            <ResultDisplay
              label={tr("yourAge")}
              value={primaryLine}
              breakdown={
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={cn("text-muted-foreground", lang === "bn" && "font-bn")}>{tr("shareLine")}</span>
                  <ShareButton title={primaryLine} />
                </div>
              }
            />
          ) : (
            <ResultDisplay label={altPrimary.label} value={altPrimary.value} />
          )}

          {/* Totals grid */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label={tr("totalMonths")} value={fmtThousands(result.totals.months)} lang={lang} />
            <StatCard label={tr("totalWeeks")} value={fmtThousands(result.totals.weeks)} lang={lang} />
            <StatCard label={tr("totalDays")} value={fmtThousands(result.totals.days)} lang={lang} />
            <StatCard label={tr("totalHours")} value={fmtThousands(result.totals.hours)} lang={lang} />
          </div>

          {/* Additional info */}
          <div className="rounded-xl border border-border bg-muted/40 p-5">
            <ul className="grid gap-3 sm:grid-cols-2">
              <InfoRow
                icon={<Cake size={16} className="text-accent" />}
                label={tr("nextBirthday")}
                value={`${tr("daysUntil").replace("{n}", num(result.nextBirthday.daysUntil))} (${tr("on")} ${lang === "bn" ? toBengaliNumerals(format(result.nextBirthday.date, "d MMMM yyyy")) : format(result.nextBirthday.date, "d MMMM yyyy")})`}
                lang={lang}
              />
              <InfoRow
                icon={<CalendarIcon size={16} className="text-accent" />}
                label={tr("dayBorn")}
                value={result.dayOfWeekBorn}
                lang={lang}
              />
              <InfoRow
                icon={<Star size={16} className="text-accent" />}
                label={tr("zodiac")}
                value={`${ZODIAC_LABEL[result.zodiac].symbol} ${ZODIAC_LABEL[result.zodiac][lang]}`}
                lang={lang}
              />
              {bn && (
                <InfoRow
                  icon={<span className="text-base leading-none">🇧🇩</span>}
                  label={tr("bnAgeLabel")}
                  value={`${num(bn.age)} ${tr("years")} (${tr("banglaShon")}: ${num(bn.birthYear)} → ${num(bn.currentYear)})`}
                  lang={lang}
                />
              )}
              {hj && (
                <InfoRow
                  icon={<Moon size={16} className="text-accent" />}
                  label={tr("hijriAgeLabel")}
                  value={`${num(hj.age)} ${tr("years")} (${num(hj.birthYear)} → ${num(hj.currentYear)} AH)`}
                  lang={lang}
                />
              )}
            </ul>
          </div>

          <div className="flex justify-end">
            <CopyResultButton text={primaryLine} />
          </div>
        </motion.div>
      )}

      {/* Content */}
      <article className="mt-12 space-y-10">
        <Section title={tr("howUseTitle")} lang={lang}>
          <ol className={cn("list-decimal space-y-2 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {STR.howUse[lang].map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </Section>

        <Section title={tr("howCalcTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{STR.howCalc[lang]}</p>
        </Section>

        <Section title={tr("bnExplainTitle")} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{STR.bnExplain[lang]}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ------------ Small UI helpers ------------ */

function StatCard({ label, value, lang }: { label: string; value: string; lang: "en" | "bn" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl border border-border bg-card p-4"
    >
      <p className={cn("text-xs font-medium uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn normal-case")}>
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </motion.div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  lang,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  lang: "en" | "bn";
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent/10">
        {icon}
      </span>
      <div className="min-w-0">
        <p className={cn("text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</p>
        <p className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{value}</p>
      </div>
    </li>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
